import { StrictMode, useEffect, useMemo, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom/client';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { App } from './app/app';
import { Loading } from './app/components';
import { toast, Toaster } from './app/components/ui/toaster';
import './styles.css';

import { CoteraApiV2 } from './app/etc/api/typed-client';
import { config } from '@cotera/client/config/config';
import * as era from '@cotera/era';
import * as sdk from '@cotera/sdk/core';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthKitProvider, useAuth } from '@workos-inc/authkit-react';
import { FilterProvider } from './app/components/contexts/filters';
import { ErrorBoundary } from './app/error-boundary';
import {
  AnalyticsClient,
  Instrumentation,
  InstrumentationProvider,
  useAnalytics,
} from './app/etc';
import { CoteraDuckDB, createLazyDuckDb } from './app/etc/duckdb';
import { ensurePylonWidget } from './app/etc/pylon';
import { captureSignupPromptFromUrl } from './app/etc/signup-prompt';
import { browserHistory } from './app/history';
import { ActionTrackingAdapter } from './v0/actions/framework/action-tracking';
import { ActionsRegistryProvider } from './v0/actions/framework/context';
import { ActionLogger, LoggerProvider } from './v0/actions/framework/logger';
import { JotaiProvider } from './v0/actions/framework/watchable';
import { useClient } from './app/hooks/use-client';
import { WorkOsRedirectState } from './app/pages/workos';
import { WatchablesFactoryProvider } from './app/v1/watchables/watchables-factory-context';
import { bootstrapPwaUpdates } from './app/etc/pwa-update';

const SIGNUP_REFERRAL_CODE_KEY = 'cotera:signup_referral_code';

Instrumentation.init();
AnalyticsClient.init();
ensurePylonWidget();
captureSignupPromptFromUrl();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
});

window.COTERA_DUCKDB = null;

// Lazy-loaded DuckDB - only initializes when first awaited (e.g., when visiting datasets/reports)
// The window.COTERA_DUCKDB global is set automatically when the db is ready
const initedDb = createLazyDuckDb();

window.era = era;
window.sdk = sdk;

const RootApp: React.FC = () => {
  const [isReloadingForUpdate, setIsReloadingForUpdate] = useState(false);
  const updateToastIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    return bootstrapPwaUpdates({
      onApplyingUpdate: () => {
        setIsReloadingForUpdate(true);
      },
      onUpdateAvailable: (applyUpdate) => {
        if (updateToastIdRef.current !== null) {
          return;
        }

        updateToastIdRef.current = toast.info(
          'A new version of Cotera is available',
          {
            description: 'Update now to get the latest features and fixes.',
            duration: Infinity,
            button: {
              label: 'Update',
              onClick: () => {
                updateToastIdRef.current = null;
                void applyUpdate();
              },
            },
          }
        );
      },
    });
  }, []);

  if (isReloadingForUpdate) {
    return (
      <Loading.Boot
        message={
          <p className="text-sm text-muted-foreground">
            Updating Cotera to the latest version...
          </p>
        }
      />
    );
  }

  return (
    <HistoryRouter history={browserHistory as any}>
      {/*react router no longer exports the History type*/}
      <InstrumentationProvider>
        <LoggerProvider>
          <QueryClientProvider client={queryClient}>
            <WatchablesFactoryProvider>
              <ErrorBoundary>
                <ActionsWithTracking>
                  <FilterProvider>
                    <JotaiProvider>
                      <AuthKitProvider
                        clientId={config.workosClientId}
                        apiHostname={config.workosApiHostname}
                        devMode={isWorkosDevModeHost}
                        onRefreshFailure={({ signIn }) => {
                          void signIn();
                        }}
                        onRedirectCallback={async ({ state }) => {
                          // Invalidate whoami query after WorkOS callback is processed
                          await queryClient.invalidateQueries({
                            queryKey: ['whoami'],
                          });

                          try {
                            // Type cast valid assuming redirects originate from WorkOSLogin and WorkOSSignup components
                            const redirectState = state as WorkOsRedirectState;
                            if (redirectState.t === 'sign-in') {
                              let returnTo = redirectState.returnTo;
                              if (returnTo === undefined) {
                                returnTo =
                                  sessionStorage.getItem(
                                    'cotera:cli_return_to'
                                  ) ?? undefined;
                                if (returnTo !== undefined) {
                                  sessionStorage.removeItem(
                                    'cotera:cli_return_to'
                                  );
                                }
                              }

                              if (returnTo !== undefined) {
                                if (returnTo.startsWith('/cli-login')) {
                                  window.location.href = returnTo;
                                } else {
                                  browserHistory.replace(returnTo);
                                }
                              }
                            } else if (
                              redirectState.t === 'sign-up' &&
                              redirectState.referralCode !== null
                            ) {
                              sessionStorage.setItem(
                                SIGNUP_REFERRAL_CODE_KEY,
                                redirectState.referralCode
                              );
                            }
                          } catch (e) {
                            // in case type cast was not valid
                            const logger = new ActionLogger();
                            logger.error(
                              `Error reading Work OS Redirect State (state = \`${state}\`).`
                            );
                          }
                        }}
                      >
                        <SignupReferralHandler>
                          <App initedDb={initedDb} />
                        </SignupReferralHandler>
                      </AuthKitProvider>
                    </JotaiProvider>
                  </FilterProvider>
                </ActionsWithTracking>
              </ErrorBoundary>
            </WatchablesFactoryProvider>
            <Toaster />
          </QueryClientProvider>
        </LoggerProvider>
      </InstrumentationProvider>
    </HistoryRouter>
  );
};

const authHost = window.location.hostname;
const isWorkosDevModeHost =
  authHost === 'localhost' ||
  authHost.endsWith('.ngrok-free.app') ||
  authHost.endsWith('.ngrok-free.dev');

declare global {
  interface Window {
    sdk: any;
    era: any;
    COTERA_DUCKDB: CoteraDuckDB | null;
    COTERA_CLIENT: CoteraApiV2 | null;
    gtag?: (
      command: 'event' | 'config' | 'js' | 'set',
      action: string | Date,
      params?: Record<string, unknown>
    ) => void;
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const ActionsWithTracking = ({ children }: { children: React.ReactNode }) => {
  const analytics = useAnalytics();
  const defaultAdapter = useMemo<ActionTrackingAdapter>(
    () => ({
      track: ({ action, payload, result, metadata }) => {
        analytics.trackAction(action, payload, result, metadata ?? undefined);
      },
    }),
    [analytics]
  );
  return (
    <ActionsRegistryProvider actions={[]} trackingAdapter={defaultAdapter}>
      {children}
    </ActionsRegistryProvider>
  );
};

/**
 * Handles recording any referrals that occur upon redirecting from WorkOS signup.
 *
 * This code here seems unidiomatic (a wrapper component and session storage) but is necessary for two reasons:
 *  - The WorkOS onRedirectCallback will need to invoke a cotera endpoint (recordReferral), which
 *    and endpoints (via useClient) can only be called within the context of a component
 *  - the cotera client (from useClient) calls useAuth which can only be invoked from
 *    a child component of AuthKitProvider
 */
const SignupReferralHandler: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isLoading } = useAuth();
  const client = useClient();

  useEffect(() => {
    void (async () => {
      const pendingReferral = sessionStorage.getItem(SIGNUP_REFERRAL_CODE_KEY);
      if (!isLoading && user !== null && pendingReferral !== null) {
        // This sleep does not block anything user-facing.
        // And is necessary to prevent a race condition where the api backend is
        // called before work os registration is finished
        await sleep(1000);

        const result = await client.u.onboarding.recordReferral({
          referralCode: pendingReferral,
        });
        if (result.isOk()) {
          sessionStorage.removeItem(SIGNUP_REFERRAL_CODE_KEY);
        }
      }
    })();
  }, [user, client, isLoading]);

  return <>{children}</>;
};

root.render(
  <StrictMode>
    <RootApp />
  </StrictMode>
);

const sleep: (ms: number) => Promise<void> = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));
